import {
  Alert,
  Button,
  DatePicker,
  DateRangePicker,
  DateRangePickerProps,
  ExpandableSection,
  FormField,
  Input,
  Link,
  SegmentedControl,
  Select,
  SelectProps,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import { EventInput, ShiftInput, ShiftTimingType } from '@amzn/red-velvet-api';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildDateTimeFromParts,
  toISODateString,
  toLocalTimeString,
} from '../../../../utils/dateFormatting';
import { OptionalLabel } from './Common';
import { useTimezone } from '../../../../hooks/timezone';
import { hasSameUtcOffset } from '../../../../utils/timezone';
import { formatMinutes } from '../../../shifts/timeModal';

const MS_PER_MINUTE = 60000;
const SHIFT_NAME_MAX_CHAR = 128;
const MAX_VOLUNTEERS_LIMIT = 2147483647; // Max signed 32-bit integer

const TIME_OPTIONS: SelectProps.Option[] = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const label = `${hour12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
  return { label, value };
});

export interface ShiftDetailsComponentProps {
  shift: ShiftInput;
  index: number;
  updateShift: (index: number, shift: ShiftInput) => void;
  onRemove?: (index: number) => void;
  errors?: Record<string, string>;
  expandOverride?: boolean;
  expandToggleKey?: number;
  isReadOnly?: boolean;
  eventFields: EventInput;
  onShiftTypeInfoClick?: () => void;
  metricPrefix: string;
}

export function ShiftDetailsComponent({
  shift,
  index,
  updateShift,
  onRemove,
  errors,
  expandOverride,
  expandToggleKey,
  isReadOnly,
  eventFields,
  onShiftTypeInfoClick,
  metricPrefix,
}: ShiftDetailsComponentProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const userTimezone = useTimezone();

  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (expandOverride !== undefined) {
      setExpanded(expandOverride);
    }
  }, [expandToggleKey, expandOverride]);

  const shiftType = shift.shiftTimingType ?? ShiftTimingType.SCHEDULED;
  const isFlexible = shiftType !== ShiftTimingType.SCHEDULED;
  const [name, setName] = useState(shift.name ?? '');
  const [date, setDate] = useState(toISODateString(shift.startDateTime));
  const initStart = toLocalTimeString(shift.startDateTime);
  const initEnd = toLocalTimeString(shift.endDateTime);
  const timesUnset = isFlexible
    ? initStart === '00:00'
    : initStart === '00:00' && initEnd === '00:00';
  const [startTime, setStartTime] = useState(timesUnset ? '' : initStart);
  const [endTime, setEndTime] = useState(
    isFlexible && initEnd === '23:59' ? '' : timesUnset ? '' : initEnd
  );
  const [startDate, setStartDate] = useState(toISODateString(shift.startDateTime));
  const [endDate, setEndDate] = useState(() => {
    const tomorrow = new Date(shift.startDateTime);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return toISODateString(shift.endDateTime) || toISODateString(tomorrow);
  });
  const [dateRange, setDateRange] = useState<DateRangePickerProps.Value | null>(() => {
    const sd = toISODateString(shift.startDateTime);
    const ed = toISODateString(shift.endDateTime);
    return sd && ed ? { type: 'absolute', startDate: sd, endDate: ed } : null;
  });
  const [defaultShiftLength, setDefaultShiftLength] = useState(() => {
    const mins = shift.shiftLength;
    return mins != null && mins > 0 ? formatMinutes(mins) : '';
  });
  const [maxVolunteers, setMaxVolunteers] = useState(
    shift.maxSignup != null && shift.maxSignup > 0 ? String(shift.maxSignup) : ''
  );
  const [endTimeError, setEndTimeError] = useState('');
  const [shiftLengthError, setShiftLengthError] = useState('');

  const shiftRef = useRef(shift);
  shiftRef.current = shift;

  const updateParent = (overrides: Partial<ShiftInput>) => {
    updateShift(index, { ...shiftRef.current, ...overrides });
  };

  const setShiftType = (type: string) => {
    const now = new Date();
    if (type === 'flexible') {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const sd = toISODateString(now);
      const ed = toISODateString(tomorrow);
      setStartDate(sd);
      setEndDate(ed);
      setStartTime('');
      setEndTime('');
      setDateRange({ type: 'absolute', startDate: sd, endDate: ed });
      setDefaultShiftLength('');
      updateParent({
        shiftTimingType: type as ShiftTimingType,
        startDateTime: new Date(`${sd}T00:00`),
        endDateTime: new Date(`${ed}T23:59`),
        shiftLength: 0,
      });
    } else {
      const today = toISODateString(now);
      setDate(today);
      setStartTime('');
      setEndTime('');
      const midnight = new Date(`${today}T00:00`);
      updateParent({
        shiftTimingType: type as ShiftTimingType,
        startDateTime: midnight,
        endDateTime: midnight,
        shiftLength: 0,
      });
    }
  };

  const isScheduled = shiftType === ShiftTimingType.SCHEDULED;

  const formatTimeLabel = (time: string) =>
    TIME_OPTIONS.find((o) => o.value === time)?.label ?? time;

  const headerDescription = isScheduled
    ? `${date || t('shiftStep.noDateSet')} • ${startTime && endTime ? `${formatTimeLabel(startTime)} - ${formatTimeLabel(endTime)}` : t('shiftStep.noTimeSet')}`
    : `${startDate || t('shiftStep.noDateSet')} - ${endDate || t('shiftStep.noDateSet')}${startTime && endTime ? ` • ${formatTimeLabel(startTime)} - ${formatTimeLabel(endTime)}` : ''}`;

  return (
    <ExpandableSection
      expanded={expanded}
      onChange={({ detail }) => setExpanded(detail.expanded)}
      headerDescription={headerDescription}
      headerText={name || t('shiftStep.defaultShiftName')}
      variant="container"
      headerActions={
        onRemove && !isReadOnly ? (
          <Button
            variant="icon"
            iconName="close"
            ariaLabel={t('shiftStep.removeShift')}
            data-aci-analytics-name={`${metricPrefix}_shiftRemoved`}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(index);
            }}
          />
        ) : undefined
      }
    >
      <SpaceBetween size="l">
        <FormField
          description={t('shiftStep.shiftNameDescription')}
          label={<OptionalLabel>{t('shiftStep.shiftName')}</OptionalLabel>}
          errorText={errors?.[`shift_${index}_name`]}
        >
          <Input
            placeholder={t('shiftStep.defaultShiftName')}
            value={name}
            onChange={({ detail }) => {
              if (detail.value.length > SHIFT_NAME_MAX_CHAR) return;
              setName(detail.value);
            }}
            onBlur={() => updateParent({ name })}
            readOnly={isReadOnly}
          />
        </FormField>

        <hr data-volunteer-center-hr />

        <FormField
          label={t('shiftStep.shiftType')}
          info={
            onShiftTypeInfoClick && (
              <Link
                variant="info"
                data-aci-analytics-name="eventCreation_shiftTypeInfoOpened"
                onFollow={(e) => {
                  e.preventDefault();
                  onShiftTypeInfoClick();
                }}
              >
                {t('misc.info')}
              </Link>
            )
          }
        >
          <SegmentedControl
            options={[
              {
                iconName: 'calendar',
                id: 'scheduled',
                text: t('shiftStep.scheduledShift'),
                disabled: isReadOnly,
              },
              {
                iconName: 'calendar',
                id: 'flexible',
                text: t('shiftStep.flexibleShift'),
                disabled: isReadOnly,
              },
            ]}
            selectedId={shiftType}
            onChange={({ detail }) => setShiftType(detail.selectedId)}
          />
        </FormField>

        {isScheduled ? (
          <>
            <FormField
              description={t('shiftStep.shiftDateDescription')}
              label={t('shiftStep.shiftDate')}
              errorText={errors?.[`shift_${index}_date`]}
            >
              <DatePicker
                placeholder="YYYY/MM/DD"
                value={date}
                onChange={({ detail }) => setDate(detail.value)}
                onBlur={() => {
                  const start = buildDateTimeFromParts(date, startTime);
                  const end = buildDateTimeFromParts(date, endTime);
                  const raw = date ? new Date(`${date}T00:00`) : undefined;
                  const midnight = raw && !isNaN(raw.getTime()) ? raw : undefined;
                  updateParent({
                    startDateTime: start ?? midnight ?? shiftRef.current.startDateTime,
                    endDateTime: end ?? midnight ?? shiftRef.current.endDateTime,
                  });
                }}
                i18nStrings={{
                  previousMonthAriaLabel: t('shiftStep.previousMonthAriaLabel'),
                  nextMonthAriaLabel: t('shiftStep.nextMonthAriaLabel'),
                  todayAriaLabel: t('shiftStep.todayAriaLabel'),
                  openCalendarAriaLabel: (selectedDate) =>
                    selectedDate
                      ? t('shiftStep.chooseDateAriaLabel', { selectedDate })
                      : t('shiftStep.chooseDateDefaultAriaLabel'),
                }}
                readOnly={isReadOnly}
              />
            </FormField>
            <SpaceBetween direction="horizontal" size="l">
              <div style={{ minWidth: 240 }}>
                <FormField
                  description={eventFields.timeZone}
                  label={t('shiftStep.shiftStartTime')}
                  errorText={errors?.[`shift_${index}_startTime`]}
                >
                  <Select
                    selectedOption={TIME_OPTIONS.find((o) => o.value === startTime) ?? null}
                    options={TIME_OPTIONS}
                    placeholder={t('shiftStep.selectTimePlaceholder')}
                    filteringType="auto"
                    onChange={({ detail }) => {
                      const val = detail.selectedOption.value ?? '';
                      setStartTime(val);
                      if (endTime && endTime <= val) {
                        setEndTimeError(t('shiftStep.endTimeMustBeAfterStartTime'));
                      } else {
                        setEndTimeError('');
                      }
                      const start = buildDateTimeFromParts(date, val);
                      if (!start) return;
                      const end = endTime ? buildDateTimeFromParts(date, endTime) : null;
                      const mins = end
                        ? Math.floor((end.getTime() - start.getTime()) / MS_PER_MINUTE)
                        : 0;
                      updateParent({ startDateTime: start, shiftLength: mins > 0 ? mins : 0 });
                    }}
                    readOnly={isReadOnly}
                  />
                </FormField>
              </div>
              <div style={{ minWidth: 240 }}>
                <FormField
                  description={eventFields.timeZone}
                  label={t('shiftStep.shiftEndTime')}
                  errorText={endTimeError || errors?.[`shift_${index}_endTime`]}
                >
                  <Select
                    selectedOption={TIME_OPTIONS.find((o) => o.value === endTime) ?? null}
                    options={TIME_OPTIONS}
                    placeholder={t('shiftStep.selectTimePlaceholder')}
                    filteringType="auto"
                    onChange={({ detail }) => {
                      const val = detail.selectedOption.value ?? '';
                      setEndTime(val);
                      if (startTime && val <= startTime) {
                        setEndTimeError(t('shiftStep.endTimeMustBeAfterStartTime'));
                      } else {
                        setEndTimeError('');
                      }
                      const end = buildDateTimeFromParts(date, val);
                      if (!end) return;
                      const start = startTime ? buildDateTimeFromParts(date, startTime) : null;
                      const mins = start
                        ? Math.floor((end.getTime() - start.getTime()) / MS_PER_MINUTE)
                        : 0;
                      updateParent({ endDateTime: end, shiftLength: mins > 0 ? mins : 0 });
                    }}
                    readOnly={isReadOnly}
                  />
                </FormField>
              </div>
            </SpaceBetween>
            {eventFields.timeZone &&
            !hasSameUtcOffset(eventFields.timeZone, userTimezone, shift.startDateTime) ? (
              <Alert
                type="warning"
                i18nStrings={{
                  warningIconAriaLabel: t('shiftStep.timeZoneMismatchAriaLabel'),
                }}
              >
                {t('shiftStep.timeZoneMismatch', {
                  userTimezone: userTimezone,
                  eventTimezone: eventFields.timeZone,
                  interpolation: { escapeValue: false },
                })}
              </Alert>
            ) : null}
          </>
        ) : (
          <>
            <SpaceBetween direction="horizontal" size="l">
              <FormField
                label={t('shiftStep.shiftDateRange')}
                description={t('shiftStep.shiftDateRangeDescription')}
                errorText={
                  errors?.[`shift_${index}_startDate`] || errors?.[`shift_${index}_endDate`]
                }
              >
                <DateRangePicker
                  value={dateRange}
                  onChange={({ detail }) => {
                    setDateRange(detail.value);
                    if (detail.value?.type === 'absolute') {
                      const { startDate: sd, endDate: ed } = detail.value;
                      setStartDate(sd);
                      setEndDate(ed);
                      if (sd === ed && startTime && endTime && endTime <= startTime) {
                        setEndTimeError(t('shiftStep.endTimeMustBeAfterStartTime'));
                      } else {
                        setEndTimeError('');
                      }
                      const updates: Partial<ShiftInput> = {};
                      const start = buildDateTimeFromParts(sd, startTime || '00:00');
                      const end = buildDateTimeFromParts(ed, endTime || '23:59');
                      if (start) updates.startDateTime = start;
                      if (end) updates.endDateTime = end;
                      updateParent(updates);
                    }
                  }}
                  relativeOptions={[]}
                  isValidRange={(range) => {
                    if (range?.type === 'absolute') {
                      if (!range.startDate || !range.endDate) {
                        return { valid: false, errorMessage: t('shiftStep.dateRangeIncomplete') };
                      }
                      if (range.startDate > range.endDate) {
                        return {
                          valid: false,
                          errorMessage: t('shiftStep.endDateMustBeAfterStartDate'),
                        };
                      }
                    }
                    return { valid: true };
                  }}
                  i18nStrings={
                    t('absoluteDatePicker', {
                      returnObjects: true,
                    }) as DateRangePickerProps.I18nStrings
                  }
                  placeholder="YYYY/MM/DD – YYYY/MM/DD"
                  dateOnly
                  rangeSelectorMode="absolute-only"
                  expandToViewport
                  readOnly={isReadOnly}
                />
              </FormField>
              <FormField
                label={t('shiftStep.defaultShiftLength')}
                description={t('shiftStep.defaultShiftLengthDescription')}
                errorText={shiftLengthError || errors?.[`shift_${index}_shiftLength`]}
              >
                <Input
                  placeholder="hh:mm"
                  inputMode="numeric"
                  value={defaultShiftLength}
                  onChange={({ detail }) => {
                    const rawTime = (detail.value.match(/[\d:]/g) || []).join('');
                    if ((rawTime.match(/:/g) || []).length > 1) return;
                    const parts = rawTime.split(':');
                    if (parts[0].length > 2 || (parts[1] !== undefined && parts[1].length > 2))
                      return;
                    setDefaultShiftLength(rawTime);
                    const h = parseInt(parts[0]) || 0;
                    const m = parts[1] !== undefined ? parseInt(parts[1]) || 0 : 0;
                    const err =
                      parts[1] !== undefined && m > 59
                        ? t('shiftStep.invalidMinutes')
                        : h * 60 + m > 720
                          ? t('shiftStep.defaultShiftLengthExceeded')
                          : '';
                    setShiftLengthError(err);
                  }}
                  onBlur={() => {
                    const parts = defaultShiftLength.split(':');
                    if (parts[1] !== undefined && parts[1].length !== 2) {
                      setShiftLengthError(t('shiftStep.invalidShiftLengthFormat'));
                      return;
                    }
                    const hours = parseInt(parts[0]) || 0;
                    const mins = parseInt(parts[1]) || 0;
                    if (mins > 59) {
                      setShiftLengthError(t('shiftStep.invalidMinutes'));
                      return;
                    }
                    const total = hours * 60 + mins;
                    if (total > 720) {
                      setShiftLengthError(t('shiftStep.defaultShiftLengthExceeded'));
                      return;
                    }
                    if (total <= 0) {
                      setShiftLengthError(t('error.shiftLengthRequired'));
                      return;
                    }
                    setShiftLengthError('');
                    updateParent({ shiftLength: total });
                  }}
                  readOnly={isReadOnly}
                />
              </FormField>
            </SpaceBetween>
            <SpaceBetween direction="horizontal" size="l">
              <FormField
                description={eventFields.timeZone}
                label={<OptionalLabel>{t('shiftStep.flexibleStartTime')}</OptionalLabel>}
              >
                <Select
                  selectedOption={TIME_OPTIONS.find((o) => o.value === startTime) ?? null}
                  options={TIME_OPTIONS}
                  placeholder={t('shiftStep.selectTimePlaceholder')}
                  filteringType="auto"
                  onChange={({ detail }) => {
                    const val = detail.selectedOption.value ?? '';
                    setStartTime(val);
                    if (startDate === endDate && endTime && endTime <= val) {
                      setEndTimeError(t('shiftStep.endTimeMustBeAfterStartTime'));
                    } else {
                      setEndTimeError('');
                    }
                    const start = buildDateTimeFromParts(startDate, val);
                    if (start) updateParent({ startDateTime: start });
                  }}
                  readOnly={isReadOnly}
                />
              </FormField>
              <FormField
                description={eventFields.timeZone}
                label={<OptionalLabel>{t('shiftStep.flexibleEndTime')}</OptionalLabel>}
                errorText={endTimeError || errors?.[`shift_${index}_endTime`]}
              >
                <Select
                  selectedOption={TIME_OPTIONS.find((o) => o.value === endTime) ?? null}
                  options={TIME_OPTIONS}
                  placeholder={t('shiftStep.selectTimePlaceholder')}
                  filteringType="auto"
                  onChange={({ detail }) => {
                    const val = detail.selectedOption.value ?? '';
                    setEndTime(val);
                    if (startDate === endDate && startTime && val <= startTime) {
                      setEndTimeError(t('shiftStep.endTimeMustBeAfterStartTime'));
                    } else {
                      setEndTimeError('');
                    }
                    const end = buildDateTimeFromParts(endDate, val);
                    if (end) updateParent({ endDateTime: end });
                  }}
                  readOnly={isReadOnly}
                />
              </FormField>
            </SpaceBetween>
          </>
        )}

        <hr data-volunteer-center-hr />

        <FormField
          description={t('shiftStep.maxVolunteersDescription')}
          label={<OptionalLabel>{t('shiftStep.maxVolunteers')}</OptionalLabel>}
        >
          <Input
            placeholder="0"
            inputMode="numeric"
            value={maxVolunteers}
            onChange={({ detail }) => {
              if (detail.value === '') {
                setMaxVolunteers('');
                return;
              }
              if (!/^\d+$/.test(detail.value)) return;
              const parsed = parseInt(detail.value);
              if (parsed > MAX_VOLUNTEERS_LIMIT) return;
              setMaxVolunteers(String(parsed));
            }}
            onBlur={() =>
              updateParent({ maxSignup: maxVolunteers === '' ? 0 : parseInt(maxVolunteers) })
            }
            readOnly={isReadOnly}
          />
        </FormField>

        <hr data-volunteer-center-hr />
      </SpaceBetween>
    </ExpandableSection>
  );
}
